// The terms, on a page rather than in a dialog.
//
// **Derived from `PrivacyPage` and deliberately identical to it**: two legal
// documents linked side by side that are set differently read as one of them
// having been taken less seriously. Same MDX pipeline, same frame, same close —
// the only differences are which document is loaded and what the heading says.
// Since U9 the "same" is literal: both pages render through `documentPage.tsx`.
//
// Like the privacy notice, it must be reachable **before** anybody signs in.
// See `App.tsx`: both are rendered above the invite-only gate, because a
// document about what you are agreeing to is worth nothing if you can only
// read it after agreeing.

import { Footer } from "./Footer";
import { DOC_COMPONENTS, DocumentPage } from "./documentPage";
import { useEffect, useState, type ComponentType } from "react";
import type { MDXComponents } from "mdx/types";

/** One entry per locale, dynamically imported — see `content/rules/index.ts`. */
const LOAD: Record<string, () => Promise<{ default: ComponentType<{ components?: MDXComponents }> }>> =
  {
    en: () => import("src/client/ai/content/terms/en.mdx")
  };

export function TermsPage({ onClose }: { onClose(): void }): React.ReactElement {
  const [Doc, setDoc] = useState<ComponentType<{ components?: MDXComponents }> | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    // A chunk load can fail — an offline reader, or a deploy that replaced the
    // file mid-session. Saying so beats an empty page, and it matters more here
    // than on the rules: a blank terms page reads as not having one.
    (LOAD.en ?? LOAD.en)()
      .then((module) => live && setDoc(() => module.default))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  return (
    <DocumentPage
      testid="terms-page"
      heading="Terms of use"
      backTestid="terms-back"
      onClose={onClose}
      failed={failed}
      source="src/client/ai/content/terms/en.mdx"
    >
      {Doc && <Doc components={DOC_COMPONENTS} />}
      <Footer />
    </DocumentPage>
  );
}
